import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Step, StepLabel, Stepper, useTheme } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import StepMainReq from './stepMainReq';
import StepExtractInfo from './stepExtractInfo';
import StepMultimodal from './stepMultimodal';
import StepPostProcessing from './stepPostProcessing';
import StepDataSource from './stepDatasource';
import StepLlm from './stepLlm';
import StepReview from './stepReview';
import StepProcessing from './stepProcessing';
import StepComplete from './stepComplete';
import StepDynamicSurvey from './stepDynamicSurvey';

export const evaluateCondition = (
  condition: any,
  userResponses: Record<string, any>,
): boolean => {
  if (!condition) return true;
  const { questionId, settingName, operator = 'contains', value } = condition;
  const targetKey = questionId || settingName;
  const actualVal = userResponses[targetKey];

  if (actualVal === undefined || actualVal === null) return false;

  if (operator === 'equals' || operator === 'eq') {
    return actualVal === value;
  }
  if (operator === 'notEquals' || operator === 'ne') {
    return actualVal !== value;
  }
  if (operator === 'contains' || operator === 'in') {
    if (Array.isArray(actualVal)) {
      return actualVal.includes(value);
    }
    return String(actualVal || '').includes(String(value));
  }
  if (operator === 'notContains' || operator === 'notIn') {
    if (Array.isArray(actualVal)) {
      return !actualVal.includes(value);
    }
    return !String(actualVal || '').includes(String(value));
  }
  if (operator === 'truthy' || operator === 'isTrue') {
    return Boolean(actualVal);
  }
  if (operator === 'falsy' || operator === 'isFalse') {
    return !actualVal;
  }
  return true;
};

type Props = { maxHeight?: number };

const UserSurvey = ({ maxHeight }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const { surveyId = 'quickSurvey' } = useParams<{ surveyId?: string }>();
  const confirm = useConfirm();

  const isQuickSurvey = surveyId === 'quickSurvey';

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const [questions, setQuestions] = useState<any[]>([]);
  const [step, setStep] = useState<string>('section');
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const [dataSources, setDataSources] = useState<any[]>([]);
  const [userResponses, setUserResponses] = useState<Record<string, any>>({
    mainRequirements: [],
    documentProps: ['imageInDocuments'],
    targetLanguage: 'en',
    dataSourceType: 'userUpload',
    multiModalOption: 'standardPrompts',
    externalApiSettings: {
      authenticationType: 'None',
    },
    llmOption: 'builtIn',
    llmSettings: {},
  });
  const [surveyResponseId, setSurveyResponseId] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const getSurvey = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getUserSurvey(surveyId as string);
        const qData = response?.surveyQuestions || [];
        setQuestions(qData);
        setStep('section');
        setCurrentSectionIndex(0);
        setActiveStep(0);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      getSurvey();
    }
  }, [surveyId]);

  useEffect(() => {
    const getDataSources = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getDataSourceInstances(false, false);
        setDataSources(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (userResponses.dataSourceType && (!dataSources || dataSources.length === 0)) {
      getDataSources();
    }
  }, [userResponses.dataSourceType]);

  const onUserResponseChange = (questionId: string, response: any) => {
    setUserResponses((prev) => ({ ...prev, [questionId]: response }));
  };

  // Filter sections dynamically based on dependsOn evaluation
  const visibleSections = questions.filter((sec: any) =>
    evaluateCondition(sec.dependsOn, userResponses),
  );

  const currentSection = visibleSections[currentSectionIndex];

  const stepsList = [
    ...visibleSections.map((sec: any, i: number) => sec.title || `Section ${i + 1}`),
    'Review & Process',
  ];

  const onNext = async () => {
    if (step === 'section') {
      if (currentSectionIndex + 1 < visibleSections.length) {
        const nextIdx = currentSectionIndex + 1;
        setCurrentSectionIndex(nextIdx);
        setActiveStep(nextIdx);
      } else {
        setStep('review');
        setActiveStep(visibleSections.length);
      }
    } else if (step === 'review') {
      setStep('processing');
      setActiveStep(visibleSections.length);
    } else if (step === 'processing') {
      setStep('complete');
      setActiveStep(visibleSections.length);
    }
  };

  const onBack = () => {
    if (step === 'section') {
      if (currentSectionIndex > 0) {
        const prevIdx = currentSectionIndex - 1;
        setCurrentSectionIndex(prevIdx);
        setActiveStep(prevIdx);
      } else {
        navigate('/buildOptions');
      }
    } else if (step === 'review') {
      setStep('section');
      const lastSecIdx = Math.max(0, visibleSections.length - 1);
      setCurrentSectionIndex(lastSecIdx);
      setActiveStep(lastSecIdx);
    }
  };

  const onCancel = () => {
    confirm({ description: 'Are you sure you want to cancel?', title: '' }).then(async () => {
      navigate('/buildOptions');
    });
  };

  if (loading) {
    return <ProgressIndicator />;
  }

  const calculatedHeight = containerSize?.height && topPanelSize?.height
    ? containerSize.height - topPanelSize.height - 20
    : maxHeight || 550;

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}
      ref={containerRef}
    >
      {/* Progress Stepper */}
      <Box
        ref={topPanelRef}
        sx={{
          px: 3,
          py: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(255,255,255,0.8)',
        }}
      >
        <Stepper
          activeStep={activeStep}
          sx={{
            maxWidth: 780,
            mx: 'auto',
            '& .MuiStepIcon-root.Mui-active': { color: theme.palette.primary.main },
            '& .MuiStepIcon-root.Mui-completed': { color: theme.palette.secondary.main },
            '& .MuiStepIcon-root': { fontSize: 16 },
            '& .MuiStepLabel-label': { fontSize: { xs: '9px', sm: '11px' }, fontWeight: 600 },
            '& .MuiStepConnector-line': { minWidth: { xs: 8, sm: 16 } },
          }}
        >
          {stepsList.map((label, idx) => (
            <Step key={idx}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {error && (
        <Box sx={{ px: 4, pt: 2 }}>
          <ErrorMessage error={error} />
        </Box>
      )}

      {/* Step Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', justifyContent: 'center', p: 1 }}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '66.67vw' }, height: '100%' }}>
          {step === 'section' && currentSection && (
            <>
              {currentSection.id === 'requirementsSection' ? (
                <StepMainReq
                  questions={questions}
                  userResponses={userResponses}
                  maxHeight={calculatedHeight}
                  onUserResponseChange={onUserResponseChange}
                  onBack={onBack}
                  onCancel={onCancel}
                  onNext={onNext}
                />
              ) : currentSection.id === 'extractionSection' ? (
                <StepExtractInfo
                  questions={questions}
                  userResponses={userResponses}
                  maxHeight={calculatedHeight}
                  onUserResponseChange={onUserResponseChange}
                  onBack={onBack}
                  onCancel={onCancel}
                  onNext={onNext}
                />
              ) : currentSection.id === 'multimodalSection' ? (
                <StepMultimodal
                  questions={questions}
                  userResponses={userResponses}
                  maxHeight={calculatedHeight}
                  onUserResponseChange={onUserResponseChange}
                  onBack={onBack}
                  onCancel={onCancel}
                  onNext={onNext}
                />
              ) : currentSection.id === 'postProcessingSection' ? (
                <StepPostProcessing
                  questions={questions}
                  userResponses={userResponses}
                  maxHeight={calculatedHeight}
                  onUserResponseChange={onUserResponseChange}
                  onBack={onBack}
                  onCancel={onCancel}
                  onNext={onNext}
                />
              ) : currentSection.id === 'dataSourceSection' || currentSection.questions?.some((q: any) => q.id === 'dataSourceType') ? (
                <StepDataSource
                  questions={questions}
                  userResponses={userResponses}
                  maxHeight={calculatedHeight}
                  dataSources={dataSources}
                  onUserResponseChange={onUserResponseChange}
                  onBack={onBack}
                  onCancel={onCancel}
                  onNext={onNext}
                />
              ) : currentSection.id === 'llmSection' || currentSection.questions?.some((q: any) => q.id === 'llmOption') ? (
                <StepLlm
                  questions={questions}
                  userResponses={userResponses}
                  maxHeight={calculatedHeight}
                  onUserResponseChange={onUserResponseChange}
                  onBack={onBack}
                  onCancel={onCancel}
                  onNext={onNext}
                />
              ) : (
                <StepDynamicSurvey
                  section={currentSection}
                  sectionIndex={currentSectionIndex}
                  totalSections={visibleSections.length}
                  userResponses={userResponses}
                  maxHeight={calculatedHeight}
                  onUserResponseChange={onUserResponseChange}
                  onBack={onBack}
                  onCancel={onCancel}
                  onNext={onNext}
                />
              )}
            </>
          )}

          {step === 'review' && (
            <StepReview
              questions={questions}
              userResponses={userResponses}
              maxHeight={calculatedHeight}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}

          {step === 'processing' && (
            <StepProcessing
              surveyId={surveyId}
              userResponses={userResponses}
              onSurveyResponseId={setSurveyResponseId}
              maxHeight={calculatedHeight}
              onBack={onBack}
              onCancel={onCancel}
              onNext={onNext}
            />
          )}

          {step === 'complete' && (
            <StepComplete
              surveyId={surveyId}
              responseId={surveyResponseId}
              userResponses={userResponses}
              maxHeight={calculatedHeight}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UserSurvey;
